import React, { useState, useCallback } from 'react';
import { compose, identity } from 'ramda';
import { useGetPlayerTags } from 'server';
import Form from 'js/components/common/Form';
import { useTranslations } from 'js/hooks/i18n';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { Button } from 'js/components/common/Button';
import { useMountEffect } from 'js/hooks/useMountEffect';
import { Radio } from 'js/components/common/Fields/Radio';
import { withComponent } from 'js/components/hocs/withComponent';
import { componentService } from 'js/services/component.service';
import { useGetErrorResponseMessage } from 'js/hooks/server/hooks';
import { ErrorMessage } from 'js/components/common/ErrorMessage';

import { parseTag } from './helpers';
import { useUpdatePreferences } from './hooks';
import { DEFAULT_FORM_DATA, PREFERENCE, TAG_NAME } from './constants';

import style from './GamingPreference.scss';

export const GamingPreferenceBare = () => {
    const [initialValues, setInitialValues] = useState(DEFAULT_FORM_DATA);
    const [submitError, setSubmitError] = useState();
    const [isUpdating, setIsUpdating] = useState(false);
    const getPlayerTags = useGetPlayerTags();
    const t = useTranslations('playtech.mobile.system.gamingPreference.');
    const getErrorResponseMessage = useGetErrorResponseMessage();
    const [{ isLoading, error, data }, refetch] = useDataFetch({
        params: { tagName: TAG_NAME },
        fetch: getPlayerTags,
        onError: getErrorResponseMessage,
        onResponse: parseTag,
        shouldFetch: identity,
        initialIsLoading: false
    });
    const updatePreferences = useUpdatePreferences({ refetch, setIsUpdating, setSubmitError });

    const onSubmit = useCallback((form) => {
        updatePreferences(form);
    }, [updatePreferences]);

    useMountEffect((isMounted) => {
        if (!isMounted() || isLoading) {
            return;
        }

        if (data) {
            setInitialValues({ ...data });

            return;
        }

        if (!data && !error) {
            refetch(Date.now());
        }
    }, [isLoading, data, refetch]);

    return (
        <div className={style.container}>
            <h2 className={style.title}>{t('title')}</h2>
            <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                render={({ handleSubmit }) => (
                    <form onSubmit={event => event.preventDefault()} >
                        {error || submitError && (
                            <ErrorMessage
                                animate={true}
                                error={error || submitError}
                                className={style.error}
                            />
                        )}
                        <div className={style.preferences}>
                            <Radio
                                name="preference"
                                value={PREFERENCE.CASINO}
                                className={style.preference}
                                label={t('label.casino')}
                            />
                            <Radio
                                name="preference"
                                value={PREFERENCE.SPORTS}
                                className={style.preference}
                                label={t('label.sports')}
                            />
                        </div>
                        <div className={style.submitRow}>
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                className={style.submit}
                                disabled={isLoading || isUpdating}
                            >
                                {t('submit')}
                            </Button>
                        </div>
                    </form>
                )}
            >
            </Form>
        </div>
    );
};

export const GamingPreference = compose(
    withComponent({ isForLoggedIn: true })
)(GamingPreferenceBare);

componentService.registerWebContentComponents({ GamingPreference });
